import React, { useState, useRef, useEffect } from 'react';

const AudioDemo = () => {
  const [currentVoice, setCurrentVoice] = useState('weathered');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const voices = {
    weathered: {
      label: 'American, Weathered, Male',
      file: '/audio/WEATHERED-Sun_Also_Rises_Ernest_Hemingway.epub_CHAPTER_1_BURT-NARRATOR.mp3'
    },
    thirties: {
      label: 'American, 30\'s, Male',
      file: '/audio/30s-Sun_Also_Rises,_The_-_Ernest_Hemingway.epub_CHAPTER_1-MID-30s_NARRATOR.mp3'
    }
  };

  // Update progress bar
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, [currentVoice]);

  const handleVoiceChange = (voice) => {
    setCurrentVoice(voice);
    setIsPlaying(false);
    setCurrentTime(0);
    
    // Auto-play after voice change
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, 100);
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
    }
  };

  const handleProgressClick = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && duration) {
      audioRef.current.currentTime = percentage * duration;
    }
  };

  const formatTime = (time) => {
    if (!time || !isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="audio-demo">
      <div className="audio-demo-content">
        {/* Voice Selection Buttons */}
        <div className="voice-selection">
          {Object.entries(voices).map(([key, voice]) => (
            <button
              key={key}
              className={`voice-btn ${currentVoice === key ? 'active' : ''}`}
              onClick={() => handleVoiceChange(key)}
            >
              <span className="voice-icon">🎧</span>
              {voice.label}
            </button>
          ))}
        </div>

        {/* Chapter Info */}
        <div className="chapter-info">
          <h3 className="chapter-title">Chapter 1: The Sun Also Rises</h3>
          <p className="chapter-author">Ernest Hemingway</p>
        </div>

        {/* Audio Controls */}
        <div className="audio-controls">
          {/* Progress Bar */}
          <div className="progress-container">
            <span className="time-display">{formatTime(currentTime)}</span>
            <div className="progress-bar" onClick={handleProgressClick}>
              <div 
                className="progress-fill" 
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="time-display">{formatTime(duration)}</span>
          </div>

          {/* Control Buttons */}
          <div className="control-buttons">
            <button className="control-btn" onClick={() => skip(-15)}>
              <span className="skip-text">15</span>
              <span className="skip-icon">⟲</span>
            </button>
            
            <button className="play-btn" onClick={togglePlay}>
              {isPlaying ? '⏸' : '▶️'}
            </button>
            
            <button className="control-btn" onClick={() => skip(15)}>
              <span className="skip-text">15</span>
              <span className="skip-icon">⟳</span>
            </button>
          </div>
        </div>

        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          src={voices[currentVoice].file}
          preload="metadata"
        />
      </div>
    </div>
  );
};

export default AudioDemo;